import {
  ENGAGEMENT_DOMAINS,
  ENGAGEMENT_EVENTS,
  ENGAGEMENT_PROPERTIES,
} from "./analytics-contract.js";

  const ANALYTICS_ENV_URL = "./.env";
  const ANALYTICS_ENV_KEYS = new Set([
    "UMAMI_SCRIPT_URL",
    "UMAMI_WEBSITE_ID",
  ]);
const UTM_PROPERTY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const ALLOWED_UTM_VALUES = Object.freeze({
  utm_source: new Set(["community-news", "newsletter", "outreach", "partner", "social"]),
  utm_medium: new Set(["email", "print", "qr", "referral", "social", "web"]),
  utm_campaign: new Set(["community-health", "reporting-awareness", "seasonal-outreach"]),
  utm_term: new Set(["health", "reporting"]),
  utm_content: new Set(["footer", "hero", "registration", "reporting"]),
});
const ANALYTICS_UTM_SESSION_STORAGE_KEY = "community-report-analytics-utms";
const ANALYTICS_ROOT_PATH = "/community-report";
const UMAMI_INITIALIZATION_TIMEOUT_MS = 5000;
const UMAMI_READINESS_DELAY_MS = 25;
// Keep the earliest lifecycle events and drop newer analytics work rather than
// allowing optional telemetry to consume unbounded memory.
const UMAMI_COMMAND_QUEUE_LIMIT = 32;
const ALLOWED_EVENT_NAMES = new Set([
  ...Object.values(ENGAGEMENT_EVENTS),
  "step viewed",
  "report flow started",
  "submit attempted",
  "submit failed",
  "submit succeeded",
  "reset clicked",
]);
const ALLOWED_DOMAINS = new Set(ENGAGEMENT_DOMAINS);
const ALLOWED_STATUSES = new Set([
  "accepted",
  "deduped",
  "deduplicated",
  "accepted_and_deduped",
  "partial_rejection",
  "rejected",
  "queued_offline",
  "local_queue_failed",
  "server_error",
  "preparation_failed",
  "failed",
  "malformed",
  "completed",
  "abandoned",
]);
const ALLOWED_VALIDATION_REASONS = new Set([
  "required",
  "invalid",
  "out_of_range",
  "unsupported",
  "unavailable",
  "too_many",
  "too_large",
]);
const ALLOWED_SCREEN_NAMES = [
  /^front-door$/,
  /^people-subject$/,
  /^people-sign-in$/,
  /^people-(?:self|other)-(?:feeling|good-check-in|symptoms|assessment|exposures|travel)$/,
  /^post-submit-registration$/,
  /^animals-(?:category|species|condition|count|date|location|reporter-feeling|observations)$/,
  /^environment-(?:issue|concern|location|date|reporter-feeling|observations)$/,
  /^report-location$/,
  /^done$/,
];
const umamiCommandQueue = [];
let analyticsConfigPromise = null;
let umamiConfigStarted = false;
let umamiConfigured = false;
let umamiUnavailable = false;
let umamiReadinessTimer = null;
let umamiInitializationTimer = null;
let umamiConfigAbortController = null;
let umamiScriptListenerCleanup = null;
let umamiLifecycleGeneration = 0;
let umamiQueueDispatchPending = false;
let analyticsInitialized = false;
let sessionUtmProperties = {};
let currentAnalyticsPath = ANALYTICS_ROOT_PATH;

  export function initAnalytics() {
    if (analyticsInitialized) return;
    analyticsInitialized = true;
    try {
      sessionUtmProperties = loadSessionUtmProperties();
      configureUmamiFromEnv();
      trackEvent(ENGAGEMENT_EVENTS.VISIT);
    } catch {
      disableUmamiQueue();
    }
  }

export function trackPageView(screenName) {
  try {
    const screen = safeScreenName(screenName);
    currentAnalyticsPath = screenPath(screen);
    const path = currentAnalyticsPath;

    queueUmamiCommand((umami) => umami.track((providerProperties) => ({
      ...safeProviderContext(providerProperties),
      title: `Community Report / ${screen}`,
      url: path,
    })));
  } catch {
    disableUmamiQueue();
  }
}

export function trackEvent(eventName, properties = {}) {
  try {
    const name = safeEventName(eventName);
    if (!name) return;
    const safeProperties = {
      ...scrubEventProperties(properties),
      ...sessionUtmProperties,
    };
    const path = currentAnalyticsPath;

    queueUmamiCommand((umami) => umami.track((providerProperties) => ({
      ...safeProviderContext(providerProperties),
      url: path,
      name,
      data: safeProperties,
    })));
  } catch {
    disableUmamiQueue();
  }
}

  function loadSessionUtmProperties() {
    const stored = readStoredUtmProperties();
    if (Object.keys(stored).length) return stored;

    const captured = captureUtmProperties(window.location?.search);
    if (Object.keys(captured).length) writeStoredUtmProperties(captured);
    return captured;
  }

export function captureUtmProperties(search = "") {
  const query = new URLSearchParams(String(search || ""));
  return UTM_PROPERTY_KEYS.reduce((properties, key) => {
    const value = sanitizeUtmValue(key, query.get(key));
    if (value) properties[key] = value;
    return properties;
  }, {});
}

  function readStoredUtmProperties() {
    try {
      const parsed = JSON.parse(window.sessionStorage?.getItem(ANALYTICS_UTM_SESSION_STORAGE_KEY) || "{}");
      return sanitizeUtmProperties(Object.fromEntries(
        UTM_PROPERTY_KEYS.map((key) => [key, parsed?.[key]])
      ));
    } catch {
      return {};
    }
  }

  function writeStoredUtmProperties(properties) {
    try {
      window.sessionStorage?.setItem(ANALYTICS_UTM_SESSION_STORAGE_KEY, JSON.stringify(properties));
    } catch {
      // Analytics attribution is optional and must not depend on browser storage.
    }
  }

function queueUmamiCommand(command) {
  if (browserDoNotTrackEnabled()) {
    disableUmamiQueue();
    return;
  }

  if (umamiUnavailable) return;

  const providerReady = hasUmami();
  if (
    providerReady
    && (!umamiConfigStarted || umamiConfigured)
    && !umamiQueueDispatchPending
    && umamiCommandQueue.length === 0
  ) {
    dispatchUmamiCommand(command);
    return;
  }

  if (umamiCommandQueue.length >= UMAMI_COMMAND_QUEUE_LIMIT) return;
  umamiCommandQueue.push(command);
  if (providerReady && umamiConfigStarted) {
    if (umamiConfigured) flushUmamiQueue();
    else completeUmamiInitialization(umamiLifecycleGeneration);
    return;
  }
  configureUmamiFromEnv();
}

function dispatchUmamiCommand(command, onComplete = () => {}) {
  let result;
  let then;
  try {
    result = command(window.umami);
    then = result?.then;
  } catch {
    disableUmamiQueue();
    return false;
  }

  if (typeof then !== "function") {
    return true;
  }

  new Promise((resolve, reject) => {
    then.call(result, resolve, reject);
  }).then(onComplete, () => disableUmamiQueue());
  return null;
}

  function configureUmamiFromEnv() {
    if (umamiConfigStarted || umamiConfigured || umamiUnavailable || hasUmami()) return;
    if (browserDoNotTrackEnabled()) {
      disableUmamiQueue();
      return;
    }
    umamiConfigStarted = true;
    const generation = ++umamiLifecycleGeneration;
    umamiConfigAbortController = new globalThis.AbortController();
    umamiInitializationTimer = globalThis.setTimeout(
      () => disableUmamiQueue(generation),
      UMAMI_INITIALIZATION_TIMEOUT_MS
    );

    loadAnalyticsConfig(umamiConfigAbortController.signal)
      .then((config) => {
        if (!isActiveUmamiLifecycle(generation)) return;
        if (hasUmami()) {
          completeUmamiInitialization(generation);
          return;
        }
        if (!config.UMAMI_SCRIPT_URL || !config.UMAMI_WEBSITE_ID) {
          disableUmamiQueue(generation);
          return;
        }

        loadUmamiScript(config.UMAMI_SCRIPT_URL, config.UMAMI_WEBSITE_ID, generation);
      })
      .catch(() => disableUmamiQueue(generation));
  }

  function loadAnalyticsConfig(signal) {
    if (analyticsConfigPromise) return analyticsConfigPromise;

    analyticsConfigPromise = fetch(ANALYTICS_ENV_URL, { cache: "no-store", signal })
      .then((response) => response.ok ? response.text() : "")
      .then((envText) => parseEnv(envText));

    return analyticsConfigPromise;
  }

  function parseEnv(envText) {
    return envText.split(/\r?\n/).reduce((env, line) => {
      const trimmed = line.trim();
      const separatorIndex = trimmed.indexOf("=");
      if (!trimmed || trimmed.startsWith("#") || separatorIndex === -1) return env;

      const key = trimmed.slice(0, separatorIndex).trim();
      if (!ANALYTICS_ENV_KEYS.has(key)) return env;

      let value = trimmed.slice(separatorIndex + 1).trim();
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1);
      }
      env[key] = value;
      return env;
    }, {});
  }

function flushUmamiQueue() {
  if (!hasUmami() || umamiUnavailable || umamiQueueDispatchPending) return;
  const command = umamiCommandQueue.shift();
  if (!command) {
    finishUmamiInitialization();
    return;
  }
  const outcome = dispatchUmamiCommand(command, () => {
    umamiQueueDispatchPending = false;
    flushUmamiQueue();
  });
  if (outcome === true) flushUmamiQueue();
  if (outcome === null) umamiQueueDispatchPending = true;
}

function disableUmamiQueue(generation) {
  if (generation !== undefined && !isActiveUmamiLifecycle(generation)) return;
  umamiUnavailable = true;
  umamiLifecycleGeneration += 1;
  umamiQueueDispatchPending = false;
  umamiCommandQueue.length = 0;
  if (umamiReadinessTimer !== null) {
    globalThis.clearTimeout(umamiReadinessTimer);
    umamiReadinessTimer = null;
  }
  if (umamiInitializationTimer !== null) {
    globalThis.clearTimeout(umamiInitializationTimer);
    umamiInitializationTimer = null;
  }
  if (umamiConfigAbortController !== null) {
    umamiConfigAbortController.abort();
    umamiConfigAbortController = null;
  }
  clearUmamiScriptListeners();
}

function isActiveUmamiLifecycle(generation) {
  return !umamiUnavailable && generation === umamiLifecycleGeneration;
}

function clearUmamiScriptListeners() {
  umamiScriptListenerCleanup?.();
  umamiScriptListenerCleanup = null;
}

  function loadUmamiScript(src, websiteId, generation) {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      if (hasUmami()) {
        completeUmamiInitialization(generation);
        return;
      }
      attachUmamiScriptListeners(existingScript, generation);
      awaitUmamiReadiness(generation);
      return;
    }

    const script = document.createElement("script");
    script.defer = true;
    script.src = src;
    script.setAttribute("data-auto-track", "false");
    script.setAttribute("data-exclude-search", "true");
    script.setAttribute("data-exclude-hash", "true");
    script.setAttribute("data-website-id", websiteId);
    attachUmamiScriptListeners(script, generation);
    document.head.append(script);
  }

function attachUmamiScriptListeners(script, generation) {
  const handleLoad = () => handleUmamiScriptLoad(generation);
  const handleError = () => disableUmamiQueue(generation);
  script.addEventListener("load", handleLoad, { once: true });
  script.addEventListener("error", handleError, { once: true });
  umamiScriptListenerCleanup = () => {
    script.removeEventListener("load", handleLoad);
    script.removeEventListener("error", handleError);
  };
}

  function handleUmamiScriptLoad(generation) {
    if (!isActiveUmamiLifecycle(generation)) return;
    if (browserDoNotTrackEnabled()) {
      disableUmamiQueue(generation);
      return;
    }
    awaitUmamiReadiness(generation);
  }

function awaitUmamiReadiness(generation) {
  if (!isActiveUmamiLifecycle(generation)) return;
  if (browserDoNotTrackEnabled()) {
    disableUmamiQueue(generation);
    return;
  }
  if (hasUmami()) {
    completeUmamiInitialization(generation);
    return;
  }

  if (umamiReadinessTimer !== null) globalThis.clearTimeout(umamiReadinessTimer);
  umamiReadinessTimer = globalThis.setTimeout(
    () => awaitUmamiReadiness(generation),
    UMAMI_READINESS_DELAY_MS
  );
}

function completeUmamiInitialization(generation) {
  if (!isActiveUmamiLifecycle(generation) || !hasUmami()) return;
  umamiConfigured = true;
  if (umamiReadinessTimer !== null) {
    globalThis.clearTimeout(umamiReadinessTimer);
    umamiReadinessTimer = null;
  }
  clearUmamiScriptListeners();
  flushUmamiQueue();
}

function finishUmamiInitialization() {
  if (umamiInitializationTimer !== null) {
    globalThis.clearTimeout(umamiInitializationTimer);
    umamiInitializationTimer = null;
  }
  umamiConfigAbortController = null;
}

  function hasUmami() {
    try {
      return Boolean(window.umami && typeof window.umami.track === "function");
    } catch {
      return false;
    }
  }

  function browserDoNotTrackEnabled() {
    try {
      const dntValues = [
        window.doNotTrack,
        window.navigator?.doNotTrack,
        window.navigator?.msDoNotTrack,
      ];
      return dntValues.some((value) => {
        const normalized = String(value || "").trim().toLowerCase();
        return normalized === "1" || normalized === "yes";
      });
    } catch {
      return true;
    }
  }

function safeScreenName(screenName) {
  const screen = String(screenName || "");
  return ALLOWED_SCREEN_NAMES.some((pattern) => pattern.test(screen)) ? screen : "unknown";
}

function safeEventName(eventName) {
  const name = String(eventName || "");
  return ALLOWED_EVENT_NAMES.has(name) ? name : "";
}

function screenPath(screenName) {
  return `/community-report/${encodeURIComponent(screenName)}`;
}

function scrubEventProperties(properties) {
  if (!properties || typeof properties !== "object" || Array.isArray(properties)) return {};

  return Object.entries(properties).reduce((safe, [key, value]) => {
    const sanitizedValue = sanitizeAllowedProperty(key, value);
    if (sanitizedValue !== undefined) safe[key] = sanitizedValue;
    return safe;
  }, {});
}

function sanitizeUtmProperties(properties) {
  if (!properties || typeof properties !== "object" || Array.isArray(properties)) return {};
  return UTM_PROPERTY_KEYS.reduce((safe, key) => {
    const value = sanitizeUtmValue(key, properties[key]);
    if (value) safe[key] = value;
    return safe;
  }, {});
}

function sanitizeAllowedProperty(key, value) {
  if (
    key === ENGAGEMENT_PROPERTIES.DOMAIN
    && typeof value === "string"
    && ALLOWED_DOMAINS.has(value)
  ) return value;
  if (
    key === ENGAGEMENT_PROPERTIES.STEP
    && typeof value === "string"
    && safeScreenName(value) === value
  ) return value;
  if (
    key === ENGAGEMENT_PROPERTIES.STATUS
    && typeof value === "string"
    && ALLOWED_STATUSES.has(value)
  ) return value;
  if (key === ENGAGEMENT_PROPERTIES.ONLINE && typeof value === "boolean") return value;
  if (
    key === ENGAGEMENT_PROPERTIES.VALIDATION_REASON
    && typeof value === "string"
    && ALLOWED_VALIDATION_REASONS.has(value)
  ) {
    return value;
  }
  if (
    key === ENGAGEMENT_PROPERTIES.APP_VERSION
    && typeof value === "string"
    && /^v?\d{1,4}(?:\.\d{1,4}){0,3}(?:-[a-z0-9][a-z0-9.-]{0,23})?$/i.test(value)
  ) {
    return value;
  }
  return undefined;
}

function sanitizeUtmValue(key, value) {
  if (typeof value !== "string") return "";
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized.length > 64) return "";
  return ALLOWED_UTM_VALUES[key]?.has(normalized) ? normalized : "";
}

function safeProviderContext(providerProperties) {
  try {
    const website = providerProperties?.website;
    if (typeof website !== "string" || !website || website.length > 128) return {};
    return { website };
  } catch {
    return {};
  }
}
